import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, Button, Grid, Chip, Divider, IconButton, Paper, CircularProgress, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useSelector } from 'react-redux';
import { ReservationService } from '../api/services/reservationService';
import { RoomService } from '../api/services/roomService';
import { RateService } from '../api/services/rateService';
import { SettingsService } from '../api/services/settingsService';
import { useCurrency, formatCurrency } from '../utils/currency';
import { isUaeProperty, calculateDubaiHospitalityTax } from '../utils/dubaiHospitalityTax';
import ReservationStatusBadge from './ReservationStatusBadge';
import ApproveGovernmentModal from './ApproveGovernmentModal';

export default function ReservationDetailModal({ open, onClose, reservation, tenantId, onMoveRoomClick, onEditClick, onCancelClick, onNoShowClick, onRefundClick, onCheckInClick, onCheckOutClick, setNotification, onClearanceApproved }) {
    const { currency } = useCurrency();
    const { tenants, user } = useSelector((state) => state.auth || {});
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [clearanceLoading, setClearanceLoading] = useState(false);
    const [govModalOpen, setGovModalOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

    // Check MANAGE_CHECKIN permission
    const hasManageCheckin = React.useMemo(() => {
        if (!user) return true;
        const roleName = user.role?.name || user.roleName || '';
        if (roleName === 'Super Admin' || roleName === 'Admin') return true;

        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        const perms = rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') return p.key || p.name || p.code || '';
            return '';
        }).filter(Boolean);

        return perms.includes('*') || perms.includes('MANAGE_CHECKIN') || perms.includes('CAN_MANAGE_CHECKIN');
    }, [user]);
    const [rejectionReason, setRejectionReason] = useState('');
    const [idImagePopup, setIdImagePopup] = useState(null);
    const [roomsList, setRoomsList] = useState([]);
    const [roomTypesList, setRoomTypesList] = useState([]);
    const [ratePlansList, setRatePlansList] = useState([]);
    const [activeTaxes, setActiveTaxes] = useState([]);

    const getAllFolioItems = (resData) => {
        if (!resData) return [];
        if (Array.isArray(resData.folio)) {
            return resData.folio.flatMap(f => (f?.items || []).map(item => ({
                ...item,
                folioId: f?.id,
                reservationRoomId: f?.reservationRoomId || item.reservationRoomId,
                folioStatus: f?.status
            })));
        }
        if (resData.folio?.items && Array.isArray(resData.folio.items)) {
            return resData.folio.items;
        }
        if (Array.isArray(resData.groupFinancialSummary?.folios)) {
            return resData.groupFinancialSummary.folios.flatMap(f => (f?.items || []).map(item => ({
                ...item,
                folioId: f?.id,
                reservationRoomId: f?.reservationRoomId || item.reservationRoomId,
                folioStatus: f?.status
            })));
        }
        return Array.isArray(resData.folioCharges) ? resData.folioCharges : [];
    };

    const getFolioPaidAmount = (resData) => {
        if (!resData) return 0;
        if (resData.groupFinancialSummary?.paidAmount !== undefined && Number(resData.groupFinancialSummary.paidAmount) > 0) {
            return Number(resData.groupFinancialSummary.paidAmount);
        }
        if (Array.isArray(resData.folio)) {
            const sumPaid = resData.folio.reduce((s, f) => s + Number(f?.paidAmount || 0), 0);
            if (sumPaid > 0) return sumPaid;
        }
        if (resData.folio?.paidAmount !== undefined && Number(resData.folio.paidAmount) > 0) {
            return Number(resData.folio.paidAmount);
        }
        if (resData.financials?.totalPaid !== undefined && Number(resData.financials.totalPaid) > 0) {
            return Number(resData.financials.totalPaid);
        }
        return Number(resData.totalAdvancePaid || resData.paidAmount || resData.payment?.paidAmount || 0);
    };

    const getAdvancePaid = (resData) => {
        if (!resData) return 0;
        const directAdv = Number(resData.totalAdvancePaid || resData.advancePaid || resData.advanceAmount || resData.billing?.advancePaid || resData.payment?.advancePaid || 0);
        if (directAdv > 0) return directAdv;
        const items = getAllFolioItems(resData);
        const depositSum = items
            .filter(i => !i.isVoided && (i.type === 'Deposit' || i.category === 'Deposit' || (i.description && (i.description.toLowerCase().includes('deposit') || i.description.toLowerCase().includes('advance')))))
            .reduce((s, i) => s + Number(i.amount || 0), 0);
        return depositSum;
    };

    const hasExtraBed = (resData) => {
        if (!resData) return false;
        if (resData.extraBed || resData.hasExtraBed || resData.extra_bed) return true;
        if ((resData.rooms || []).some(r => r.extraBed || r.hasExtraBed || r.extra_bed)) return true;
        const folioItems = getAllFolioItems(resData);
        return folioItems.some(i =>
            !i.isVoided &&
            (i.category === 'extra_bed' || i.category === 'Extra Bed' || (i.description && i.description.toLowerCase().includes('extra bed')) || (i.item && i.item.toLowerCase().includes('extra bed')))
        );
    };

    const hasRoomExtraBed = (r, resData) => {
        if (!r) return false;
        if (r.extraBed || r.hasExtraBed || r.extra_bed) return true;
        const folioItems = getAllFolioItems(resData);
        return folioItems.some(i =>
            !i.isVoided &&
            (i.category === 'extra_bed' || i.category === 'Extra Bed' || (i.description && i.description.toLowerCase().includes('extra bed')) || (i.item && i.item.toLowerCase().includes('extra bed'))) &&
            (!i.reservationRoomId || i.reservationRoomId === r.id || i.reservationRoomId === r.roomId)
        );
    };

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const [rData, rtData, rpData, taxData] = await Promise.all([
                    RoomService.getAll({ noPagination: true }),
                    RoomService.getRoomTypes({ noPagination: true }),
                    RateService.getRatePlans(tenantId, { noPagination: true }),
                    SettingsService.getTaxes(tenantId, { noPagination: true }).catch(() => [])
                ]);
                const roomsArr = Array.isArray(rData) ? rData : (rData.data || rData.rooms || []);
                const rtArr = Array.isArray(rtData) ? rtData : (rtData.data || rtData.roomTypes || []);
                const rpArr = Array.isArray(rpData) ? rpData : (rpData.data || rpData.ratePlans || []);
                const taxArr = Array.isArray(taxData) ? taxData : (taxData?.data || taxData?.taxes || []);
                setRoomsList(roomsArr);
                setRoomTypesList(rtArr);
                setRatePlansList(rpArr);
                setActiveTaxes(taxArr.filter(t => t.status === 'Active'));
            } catch (err) {
                console.error("Failed to load master data", err);
            }
        };
        if (open) fetchMasterData();
    }, [open, tenantId]);

    useEffect(() => {
        if (open && reservation?.id) {
            fetchDetail(reservation.id);
        } else {
            setDetail(null);
        }
    }, [open, reservation, tenantId]);

    const fetchDetail = async (id) => {
        setLoading(true);
        try {
            const response = await ReservationService.getById(id, tenantId);
            setDetail(response.data || response);
        } catch (error) {
            console.error('Failed to fetch reservation details:', error);
            if (setNotification) {
                setNotification({ open: true, message: 'Failed to load comprehensive reservation details.', severity: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleApproveRate = async () => {
        setActionLoading(true);
        try {
            await ReservationService.approveRateOverride(data.id, tenantId);
            if (setNotification) {
                setNotification({ open: true, message: 'Rate override approved successfully.', severity: 'success' });
            }
            fetchDetail(data.id);
        } catch (error) {
            console.error('Failed to approve rate:', error);
            if (setNotification) {
                setNotification({ open: true, message: error?.response?.data?.message || 'Failed to approve rate override.', severity: 'error' });
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectRate = async () => {
        if (!rejectionReason.trim()) {
            if (setNotification) setNotification({ open: true, message: 'Rejection reason is required.', severity: 'warning' });
            return;
        }
        setActionLoading(true);
        try {
            await ReservationService.rejectRateOverride(data.id, { reason: rejectionReason }, tenantId);
            if (setNotification) {
                setNotification({ open: true, message: 'Rate override rejected successfully.', severity: 'success' });
            }
            setRejectDialogOpen(false);
            setRejectionReason('');
            fetchDetail(data.id);
        } catch (error) {
            console.error('Failed to reject rate:', error);
            if (setNotification) {
                setNotification({ open: true, message: error?.response?.data?.message || 'Failed to reject rate override.', severity: 'error' });
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleApproveGovernmentClearance = async (payload = {}) => {
        if (!data?.id) return;
        setClearanceLoading(true);
        try {
            const body = {
                approvalReference: payload.approvalReference || 'DET-2026-98124',
                notes: payload.notes || 'Verified and cleared with Dubai Police / DET portal'
            };
            await ReservationService.approveGovernmentClearance(data.id, tenantId, body);
            if (setNotification) {
                setNotification({
                    open: true,
                    message: 'Government clearance approved successfully. Reservation is now Confirmed.',
                    severity: 'success'
                });
            }
            await fetchDetail(data.id);
            if (onClearanceApproved) {
                onClearanceApproved(data.id);
            }
        } catch (error) {
            console.error('Failed to approve government clearance:', error);
            const status = error?.response?.status;
            const msg = status === 403
                ? "Permission denied: You do not have the required 'MANAGE_CHECKIN' permission to approve government status."
                : (error?.response?.data?.message || 'Failed to approve government clearance.');
            if (setNotification) {
                setNotification({
                    open: true,
                    message: msg,
                    severity: 'error'
                });
            }
        } finally {
            setClearanceLoading(false);
        }
    };

    if (!open) return null;

    // Use full detail if loaded, otherwise fallback to the partial grid data
    const data = detail || reservation;
    if (!data) return null;

    const tenant = (tenants || []).find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const isUae = currency === 'AED' || isUaeProperty(tenant) || isUaeProperty(data?.hotel) || isUaeProperty(data?.property);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Checked-in': return 'success';
            case 'Checked-out': return 'info';
            case 'Confirmed': return 'primary';
            case 'Cancelled': return 'error';
            case 'No-show': return 'warning';
            default: return 'default';
        }
    };

    const formatRooms = (res) => {
        if (!res.rooms) return res.room?.roomNumber || res.room?.name || res.roomNumber || 'N/A';
        if (Array.isArray(res.rooms)) {
            return res.rooms.map(r => typeof r === 'object' ? (r.room?.roomNumber || r.room?.name || r.roomNumber || r.name) : r).join(', ');
        }
        return String(res.rooms);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.split('T')[0];
    };

    const getNights = (res) => {
        if (res.nights !== undefined) return res.nights;
        if (res.checkIn && res.checkOut) {
            const diffTime = Math.abs(new Date(res.checkOut) - new Date(res.checkIn));
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return 0;
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        if (onClose) onClose();
    };

    return (
        <>
        <Modal open={open} onClose={handleModalClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', md: 1100 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                maxHeight: '95vh', overflowY: 'auto'
            }}>
                {/* Header Row: Title & Close */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" fontWeight="bold">Reservation Details</Typography>
                        {!loading && (
                            <ReservationStatusBadge
                                reservation={data}
                                isUae={isUae}
                                direction="row"
                                size="medium"
                            />
                        )}
                        {!loading && data.pendingApproval && (
                            <Chip
                                label="Rate Override Pending"
                                color="warning"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}><CloseIcon /></IconButton>
                </Box>

                {/* Second Row: ID and Actions */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Booking ID</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                            {data.bookingId || data.id || data._id || 'N/A'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                        {isUae && (data.governmentStatus === 'Pending' || (!data.governmentStatus && data.status === 'Pending')) && (
                            <Tooltip title={!hasManageCheckin ? "Requires MANAGE_CHECKIN permission" : "Approve government clearance with Dubai Police / DET"}>
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        startIcon={clearanceLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon fontSize="small" />}
                                        disabled={clearanceLoading || !hasManageCheckin}
                                        onClick={() => setGovModalOpen(true)}
                                    >
                                        Approve Government Status
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {onCancelClick && ['Confirmed', 'Pending'].includes(data.status) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon fontSize="small" />}
                                onClick={() => onCancelClick(data)}
                            >
                                Cancel Reservation
                            </Button>
                        )}
                        {onNoShowClick && ['Confirmed', 'Pending'].includes(data.status) && new Date(data.checkIn).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<BlockIcon fontSize="small" />}
                                onClick={() => onNoShowClick(data)}
                            >
                                Mark No-Show
                            </Button>
                        )}
                        {onCheckInClick && data.status === 'Confirmed' && (
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<CheckCircleIcon fontSize="small" />}
                                onClick={() => onCheckInClick(data)}
                            >
                                Check In Guest
                            </Button>
                        )}
                        {onCheckOutClick && data.status === 'In-house' && (
                            <Button
                                size="small"
                                variant="contained"
                                color="info"
                                startIcon={<ExitToAppIcon fontSize="small" />}
                                onClick={() => onCheckOutClick(data)}
                            >
                                Check Out Guest
                            </Button>
                        )}

                    </Box>
                </Box>

                {isUae && (data.governmentStatus === 'Pending' || (!data.governmentStatus && data.status === 'Pending')) && !loading && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                        <strong>Government Approval Required:</strong> In accordance with Dubai / UAE Tourism (DET) and local hospitality regulations, this reservation is currently pending mandatory regulatory verification before guest check-in can proceed. Authorized staff can approve status above once verified.
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={6}><Skeleton variant="text" width="80%" /><Skeleton variant="text" width="60%" /></Grid>
                            <Grid item xs={6}><Skeleton variant="text" width="80%" /><Skeleton variant="text" width="60%" /></Grid>
                            <Grid item xs={6}><Skeleton variant="text" width="80%" /><Skeleton variant="text" width="60%" /></Grid>
                            <Grid item xs={6}><Skeleton variant="text" width="80%" /><Skeleton variant="text" width="60%" /></Grid>
                        </Grid>
                        <Skeleton variant="rectangular" height={80} sx={{ mt: 2, borderRadius: 2 }} />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* LEFT COLUMN: Profile, Core Fields, Payment Ledger */}
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* 1. Guest Demographic Profile Card */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }} elevation={0}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Guest Demographic Profile</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="h6">{(data.guest?.title ? data.guest.title + ' ' : '') + (data.guest?.name || data.guestName || 'Unknown Guest')}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Email:</strong> {data.guest?.email || data.guestEmail || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Phone:</strong> {data.guest?.phone || data.guestPhone || 'N/A'}
                                </Typography>
                                {data.guest?.mobile && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Mobile:</strong> {data.guest.mobile}
                                    </Typography>
                                )}
                                {data.guest?.gender && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Gender:</strong> {data.guest.gender}
                                    </Typography>
                                )}

                                <Divider sx={{ my: 1.5, opacity: 0.6 }} />
                                <Typography variant="caption" fontWeight="bold" color="primary" display="block" gutterBottom>ADDRESS</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {[data.guest?.address, data.guest?.city, data.guest?.state, data.guest?.country].filter(Boolean).join(', ') || 'No Address Provided'}
                                    {data.guest?.zip ? ` - ${data.guest.zip}` : ''}
                                </Typography>

                                <Divider sx={{ my: 1.5, opacity: 0.6 }} />
                                <Typography variant="caption" fontWeight="bold" color="primary" display="block" gutterBottom>IDENTITY INFORMATION</Typography>
                                <Typography variant="body2" color="text.secondary"><strong>ID Type:</strong> {data.guest?.idType || 'N/A'}</Typography>
                                <Typography variant="body2" color="text.secondary"><strong>ID Number:</strong> {data.guest?.idProof || 'Not Provided'}</Typography>
                                {data.guest?.idExpiryDate && (
                                    <Typography variant="body2" color="text.secondary"><strong>Expiry:</strong> {formatDate(data.guest.idExpiryDate)}</Typography>
                                )}
                                {data.guest?.idDocumentUrl && (
                                    <Box sx={{ mt: 1 }}>
                                        <Button size="small" variant="outlined" onClick={() => setIdImagePopup(data.guest.idDocumentUrl)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                                            View Uploaded ID
                                        </Button>
                                    </Box>
                                )}

                                {(data.guest?.birthDate || data.guest?.nationality) && (
                                    <>
                                        <Divider sx={{ my: 1.5, opacity: 0.6 }} />
                                        <Typography variant="caption" fontWeight="bold" color="primary" display="block" gutterBottom>PERSONAL DETAILS</Typography>
                                        <Typography variant="body2" color="text.secondary"><strong>DOB:</strong> {formatDate(data.guest?.birthDate)}</Typography>
                                        <Typography variant="body2" color="text.secondary"><strong>Nationality:</strong> {data.guest?.nationality || 'N/A'}</Typography>
                                    </>
                                )}
                            </Paper>

                            {/* 2. Core Booking Fields Summary Panel */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }} elevation={0}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Core Booking Summary</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                                        <Typography variant="body2" fontWeight="bold">{data.bookingId || data.id}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Source / Type</Typography>
                                        <Typography variant="body2" fontWeight="bold">{data.bookingSource || 'Direct'} / {data.bookingType || 'Single'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Arrival (Check-in)</Typography>
                                        <Typography variant="body2" fontWeight="bold">{formatDate(data.checkIn)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Departure (Check-out)</Typography>
                                        <Typography variant="body2" fontWeight="bold">{formatDate(data.checkOut)}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <ReservationStatusBadge
                                        reservation={data}
                                        isUae={isUae}
                                        direction="row"
                                        size="small"
                                    />
                                    <Chip
                                        label={hasExtraBed(data) ? "Extra Bed: Yes" : "Extra Bed: No"}
                                        size="small"
                                        color={hasExtraBed(data) ? "primary" : "default"}
                                        variant={hasExtraBed(data) ? "filled" : "outlined"}
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                                        {getNights(data)} Nights
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* 5. Detailed Payment Records & Balances Ledger */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }} elevation={0}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Payment & Balances Ledger</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {(() => {
                                    const formatINR = (val) => {
                                        const num = Number(val || 0);
                                        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    };

                                    const hasRoomsArray = Array.isArray(data.rooms) && data.rooms.length > 0;
                                    const roomsBaseSum = hasRoomsArray ? data.rooms.reduce((s, r) => s + Number(r.roomTotal !== undefined ? r.roomTotal : 0), 0) : 0;
                                    const roomsDiscountSum = hasRoomsArray ? data.rooms.reduce((s, r) => s + Number(r.discounts !== undefined ? r.discounts : 0), 0) : 0;
                                    const roomsNetTaxableSum = hasRoomsArray ? data.rooms.reduce((s, r) => s + Number(r.netTaxableValue !== undefined ? r.netTaxableValue : 0), 0) : 0;
                                    const roomsTaxSum = hasRoomsArray ? data.rooms.reduce((s, r) => s + Number(r.taxTotal !== undefined ? r.taxTotal : 0), 0) : 0;

                                    // 1. Original Room Base
                                    const roomBase = Number(roomsBaseSum > 0 ? roomsBaseSum : (data.billing?.roomSubtotal || data.billing?.subtotal || (Number(data.totalAmount || 0) - Number(data.taxAmount || 0) + Number(data.discounts || 0))));

                                    // 2. Less: Final Rate Override (Pre-Tax)
                                    const rateOverride = Number(data.discounts !== undefined && Number(data.discounts) > 0 ? data.discounts : (roomsDiscountSum > 0 ? roomsDiscountSum : (data.billing?.overrideOrDiscount || data.billing?.discountAmount || data.financials?.roomOverrideOrDiscount || data.discount || 0)));

                                    // 3. Net Taxable Value
                                    const netTaxableVal = Number(data.netTaxableValue !== undefined ? data.netTaxableValue : (roomsNetTaxableSum > 0 ? roomsNetTaxableSum : (data.billing?.netTaxableValue !== undefined && Number(data.billing?.overrideOrDiscount || 0) === rateOverride ? data.billing.netTaxableValue : Math.max(0, roomBase - rateOverride))));

                                    // 4. Statutory Taxes / Fees Breakdown
                                    let uaeTaxBreakdown = null;
                                    let taxAccrued = 0;
                                    let gstPct = null;
                                    let taxLabel = '';

                                    if (isUae) {
                                        const nights = (data.checkIn && data.checkOut)
                                            ? Math.max(1, Math.round((new Date(data.checkOut) - new Date(data.checkIn)) / (1000 * 60 * 60 * 24)))
                                            : (Number(data.nights) || 1);
                                        const bedroomsCount = (data.rooms && data.rooms.length > 0) ? data.rooms.length : 1;
                                        const computedUae = calculateDubaiHospitalityTax({
                                            netBaseAmount: netTaxableVal,
                                            nights,
                                            bedroomsCount,
                                            rating: data.hotel?.rating || tenant?.rating || 4,
                                            hotelType: data.hotel?.type || tenant?.type || '',
                                            taxes: activeTaxes
                                        });

                                        const serviceChargeAmount = Number(data.serviceCharge !== undefined ? data.serviceCharge : (data.billing?.serviceCharge !== undefined ? data.billing.serviceCharge : (data.pricing?.serviceCharge !== undefined ? data.pricing.serviceCharge : computedUae.serviceCharge)));
                                        const municipalityFeeAmount = Number(data.municipalityFee !== undefined ? data.municipalityFee : (data.billing?.municipalityFee !== undefined ? data.billing.municipalityFee : (data.pricing?.municipalityFee !== undefined ? data.pricing.municipalityFee : computedUae.municipalityFee)));
                                        const vatAmount = Number(data.vat !== undefined ? data.vat : (data.billing?.vat !== undefined ? data.billing.vat : (data.pricing?.vat !== undefined ? data.pricing.vat : computedUae.vat)));
                                        const tourismDirhamAmount = Number(data.tourismDirham !== undefined ? data.tourismDirham : (data.billing?.tourismDirham !== undefined ? data.billing.tourismDirham : (data.pricing?.tourismDirham !== undefined ? data.pricing.tourismDirham : computedUae.tourismDirham)));
                                        const tdRate = Number(data.tourismDirhamRate || data.billing?.tourismDirhamRate || computedUae.tourismDirhamRate || 15);

                                        taxAccrued = serviceChargeAmount + municipalityFeeAmount + vatAmount + tourismDirhamAmount;
                                        uaeTaxBreakdown = {
                                            serviceChargeAmount,
                                            serviceChargePct: computedUae.serviceChargePct || 10,
                                            municipalityFeeAmount,
                                            municipalityFeePct: computedUae.municipalityFeePct || 7,
                                            vatAmount,
                                            vatPct: computedUae.vatPct || 5,
                                            tourismDirhamAmount,
                                            tourismDirhamRate: tdRate
                                        };
                                    } else {
                                        taxAccrued = Number(data.taxAmount !== undefined && Number(data.taxAmount) > 0 ? data.taxAmount : (data.taxTotal !== undefined && Number(data.taxTotal) > 0 ? data.taxTotal : (roomsTaxSum > 0 ? roomsTaxSum : (data.billing?.estimatedTax || 0))));
                                        gstPct = (data.taxExempt || data.taxPercentage === 0) ? null : (data.gstPercentage !== undefined && Number(data.gstPercentage) > 0 ? Number(data.gstPercentage) : (data.billing?.gstPercentage !== undefined && Number(data.billing.gstPercentage) > 0 ? Number(data.billing.gstPercentage) : (netTaxableVal > 0 && taxAccrued > 0 ? Math.round((taxAccrued / netTaxableVal) * 100) : null)));
                                        taxLabel = data.taxExempt ? 'Total Tax Accrued (Exempt)' : (gstPct !== null ? `Total Tax Accrued (${gstPct}%)` : 'Total Tax Accrued (5%)');
                                    }

                                    // 5. Subtotal (Room & Tax)
                                    const subtotalRoomAndTax = netTaxableVal + taxAccrued;

                                    // 6. Other Folio Charges (Undiscounted)
                                    const folioItems = getAllFolioItems(data);
                                    const folioIncidentals = folioItems
                                        .filter(i => {
                                            if (i.isVoided) return false;
                                            const type = (i.type || '').toLowerCase();
                                            const cat = (i.category || '').toLowerCase();
                                            const desc = (i.description || '').toLowerCase();
                                            if (['room charge', 'tax', 'payment', 'settlement', 'refund', 'deposit', 'adjust'].includes(type)) return false;
                                            if (['room charge', 'tax', 'payment', 'settlement', 'refund', 'deposit', 'discount', 'tax reversal'].includes(cat)) return false;
                                            if (desc.includes('nightly room charge') || desc.includes('gst (cgst+sgst) for room')) return false;
                                            return true;
                                        })
                                        .reduce((s, i) => s + Number(i.amount || 0), 0);
                                    const otherFolioCharges = Number(folioIncidentals > 0 ? folioIncidentals : (data.additionalCharges || data.billing?.additionalCharges || data.financials?.incidentalCharges || data.incidentalCharges || 0)) + Number(data.cancellationFee || 0);

                                    // 7. Pre-Discount Folio Total
                                    const preDiscountFolioTotal = subtotalRoomAndTax + otherFolioCharges;

                                    // 8. Less: Flat Checkout Discount
                                    const folioAdjustDiscounts = folioItems
                                        .filter(i => !i.isVoided && ((i.type === 'Adjust' && (i.category === 'Discount' || i.category === 'Tax Reversal' || (i.description && i.description.toLowerCase().includes('discount')))) || (i.category === 'Discount' && Number(i.amount) < 0)))
                                        .reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0);
                                    const flatCheckoutDiscount = Number(data.flatDiscount !== undefined && Number(data.flatDiscount) > 0 ? data.flatDiscount : (data.billing?.flatDiscount !== undefined && Number(data.billing.flatDiscount) > 0 ? data.billing.flatDiscount : (data.checkoutDiscount || data.flatCheckoutDiscount || data.billing?.checkoutDiscount || data.financials?.checkoutDiscount || data.billing?.flatCheckoutDiscount || folioAdjustDiscounts || 0)));

                                    // 9. Final Total Charges
                                    const finalTotalCharges = isUae ? preDiscountFolioTotal : Math.max(0, preDiscountFolioTotal - flatCheckoutDiscount);

                                    // 10. Less: Advance Paid
                                    const advancePaid = getAdvancePaid(data);
                                    const totalPaid = getFolioPaidAmount(data);

                                    // 11. Updated Balance Due
                                    const rawBalance = finalTotalCharges - (totalPaid > 0 ? totalPaid : advancePaid);
                                    const balanceDue = (data.paymentStatus === 'Paid' || totalPaid >= finalTotalCharges) ? 0 : Math.max(0, rawBalance);
                                    const isRefund = rawBalance < 0;

                                    const renderRow = (label, value, isBold = false) => (
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1.25,
                                            borderBottom: '1px solid #f1f5f9'
                                        }}>
                                            <Typography sx={{
                                                fontSize: isBold ? '0.875rem' : '0.845rem',
                                                color: isBold ? '#0f172a' : '#334155',
                                                fontWeight: isBold ? 700 : 400
                                            }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: isBold ? '0.9rem' : '0.845rem',
                                                color: isBold ? '#0f172a' : '#1e293b',
                                                fontWeight: isBold ? 700 : 400
                                            }}>
                                                {value}
                                            </Typography>
                                        </Box>
                                    );

                                    return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            {renderRow('Original Room Base', formatCurrency(roomBase, currency), false)}
                                            {renderRow('Less: Final Rate Override (Pre-Tax)', `-${formatCurrency(rateOverride, currency)}`, false)}
                                            {renderRow('Net Taxable Value', formatCurrency(netTaxableVal, currency), true)}
                                            {isUae && uaeTaxBreakdown ? (
                                                <>
                                                    {renderRow(`Service Charge (${uaeTaxBreakdown.serviceChargePct}%)`, formatCurrency(uaeTaxBreakdown.serviceChargeAmount, currency), false)}
                                                    {renderRow(`Dubai Municipality Fee (${uaeTaxBreakdown.municipalityFeePct}%)`, formatCurrency(uaeTaxBreakdown.municipalityFeeAmount, currency), false)}
                                                    {renderRow(`VAT (${uaeTaxBreakdown.vatPct}%)`, formatCurrency(uaeTaxBreakdown.vatAmount, currency), false)}
                                                    {renderRow(`Tourism Dirham (AED ${uaeTaxBreakdown.tourismDirhamRate} / night)`, formatCurrency(uaeTaxBreakdown.tourismDirhamAmount, currency), false)}
                                                </>
                                            ) : (
                                                renderRow(taxLabel, formatCurrency(taxAccrued, currency), false)
                                            )}
                                            {renderRow('Subtotal (Room & Tax)', formatCurrency(subtotalRoomAndTax, currency), true)}
                                            {renderRow('Other Folio Charges (Undiscounted)', formatCurrency(otherFolioCharges, currency), false)}
                                            {!isUae && renderRow('Pre-Discount Folio Total', formatCurrency(preDiscountFolioTotal, currency), true)}
                                            {!isUae && renderRow('Less: Flat Checkout Discount', `-${formatCurrency(flatCheckoutDiscount, currency)}`, false)}
                                            {renderRow('Final Total Charges', formatCurrency(finalTotalCharges, currency), true)}
                                            {renderRow('Less: Advance Paid', `-${formatCurrency(advancePaid, currency)}`, false)}
                                            {renderRow('Updated Balance Due', `${isRefund ? '-' : ''}${formatCurrency(Math.abs(balanceDue), currency)}`, true)}
                                        </Box>
                                    );
                                })()}
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">Payment Status</Typography>
                                    <Chip
                                        label={data.paymentStatus || 'Due'}
                                        size="small"
                                        color={data.paymentStatus === 'Paid' ? 'success' : 'warning'}
                                        variant="outlined"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Billing Mode</Typography>
                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{data.billingMode || data.payment?.billingMode || 'Master Bill'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                                    <Typography variant="body2">{data.paymentMethod || data.payment?.paymentMethod || 'Not Provided'}</Typography>
                                </Box>
                                {(data.paymentMethod === 'Direct Billing' || data.payment?.paymentMethod === 'Direct Billing') && data.directBillingAc && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Billing A/C</Typography>
                                        <Typography variant="body2">{data.directBillingAc}</Typography>
                                    </Box>
                                )}
                                {(() => {
                                    const folioItems = getAllFolioItems(data);
                                    const folioTxns = folioItems.filter(i => !i.isVoided && (['Payment', 'Refund', 'Deposit'].includes(i.type) || i.category === 'Payment' || i.category === 'Deposit'));
                                    const txns = [...folioTxns, ...(data.payments || [])];
                                    return txns.length > 0 ? (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Recent Transactions</Typography>
                                            {txns.map((p, i) => (
                                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, bgcolor: '#f1f5f9', p: 1, borderRadius: 1 }}>
                                                    <Typography variant="caption">{formatDate(p.date || p.createdAt)} ({p.method || p.paymentMethod || p.type || 'Payment'})</Typography>
                                                    <Typography variant="caption" fontWeight="bold" color={p.type === 'Refund' ? 'error.main' : 'text.primary'}>
                                                        {p.type === 'Refund' ? '-' : ''}{formatCurrency(p.amount, currency)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                            No explicit transaction records available.
                                        </Typography>
                                    );
                                })()}
                            </Paper>

                            {/* 5b. Room Billing Breakdown */}
                            {((data.rooms && data.rooms.length > 0) || (data.billing?.roomBreakdown && data.billing.roomBreakdown.length > 0)) && (
                                <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', mt: 3 }} elevation={0}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Room Billing Breakdown</Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    {((data.rooms && data.rooms.length > 0) ? data.rooms : data.billing.roomBreakdown).map((item, idx) => {
                                        const roomInfo = item.room ? item : ((data.rooms || []).find(r => r.roomId === item.roomId || r.id === item.roomId) || (data.rooms || [])[idx] || item);
                                        const billingItem = (data.billing?.roomBreakdown || [])[idx] || (data.billing?.roomBreakdown || []).find(b => b.roomId === item.roomId || b.roomId === item.id) || item;

                                        const roomName = roomInfo?.room?.roomNumber || roomInfo?.roomNumber || billingItem?.roomNumber || `Room ${idx + 1}`;
                                        const roomTypeName = roomInfo?.roomType?.name || roomInfo?.room?.type || billingItem?.roomType?.name || 'Room';
                                        const nights = billingItem?.nights || getNights(data) || 1;
                                        const roomLabel = `Room ${idx + 1} (${roomTypeName}) x ${nights}N`;

                                        // Prioritize exact room entity financial fields:
                                        // roomTotal, discounts, netTaxableValue, taxTotal, calculatedTotal (= netTaxableValue + taxTotal)
                                        const roomRate = Number(roomInfo?.roomTotal !== undefined ? roomInfo.roomTotal : (billingItem?.roomRate !== undefined ? billingItem.roomRate : billingItem?.roomTotal || 0));
                                        const discount = Number(roomInfo?.discounts !== undefined ? roomInfo.discounts : (billingItem?.discountApplied || 0));
                                        const netTaxable = Number(roomInfo?.netTaxableValue !== undefined ? roomInfo.netTaxableValue : (billingItem?.netTaxableValue !== undefined ? billingItem.netTaxableValue : (roomRate - discount)));
                                        const taxAmt = Number(roomInfo?.taxTotal !== undefined ? roomInfo.taxTotal : (billingItem?.roomTaxAmount !== undefined ? billingItem.roomTaxAmount : (billingItem?.estimatedTax || 0)));
                                        const finalTotal = Number(roomInfo?.calculatedTotal !== undefined ? roomInfo.calculatedTotal : (netTaxable + taxAmt));
                                        const isRoomTaxExempt = Boolean(data.taxExempt || roomInfo?.taxExempt || billingItem?.taxExempt);
                                        const gstPct = isRoomTaxExempt ? 0 : (billingItem?.gstPercentage !== undefined && billingItem.gstPercentage > 0 ? billingItem.gstPercentage : (netTaxable > 0 && taxAmt > 0 ? Math.round((taxAmt / netTaxable) * 100) : 0));

                                        let resolvedRoomUae = null;
                                        if (isUae && !isRoomTaxExempt && netTaxable > 0) {
                                            const rawBreakdown = billingItem?.uaeBreakdown || calculateDubaiHospitalityTax({
                                                netBaseAmount: netTaxable,
                                                nights: nights || 1,
                                                bedroomsCount: 1,
                                                rating: data.hotel?.rating || tenant?.rating || 4,
                                                hotelType: data.hotel?.type || tenant?.type || '',
                                                taxes: activeTaxes
                                            });

                                            if (rawBreakdown) {
                                                const scAmt = Number(billingItem?.serviceCharge !== undefined ? billingItem.serviceCharge : (rawBreakdown.serviceCharge !== undefined ? rawBreakdown.serviceCharge : (rawBreakdown.serviceChargeAmount || 0)));
                                                const scRate = Number(rawBreakdown.serviceChargeRate || rawBreakdown.serviceChargePct || 10);
                                                const mfAmt = Number(billingItem?.municipalityFee !== undefined ? billingItem.municipalityFee : (rawBreakdown.municipalityFee !== undefined ? rawBreakdown.municipalityFee : (rawBreakdown.municipalityFeeAmount || 0)));
                                                const mfRate = Number(rawBreakdown.municipalityRate || rawBreakdown.municipalityFeePct || 7);
                                                const vatAmt = Number(billingItem?.vat !== undefined ? billingItem.vat : (rawBreakdown.vat !== undefined ? rawBreakdown.vat : (rawBreakdown.vatAmount || 0)));
                                                const vatRate = Number(rawBreakdown.vatRate || rawBreakdown.vatPct || 5);
                                                let tdAmt = Number(billingItem?.tourismDirham !== undefined ? billingItem.tourismDirham : (rawBreakdown.tourismDirham !== undefined ? rawBreakdown.tourismDirham : (rawBreakdown.tourismDirhamAmount || 0)));
                                                const tdRate = Number(rawBreakdown.tourismDirhamRate || 15);

                                                if (taxAmt > 0 && Math.abs((scAmt + mfAmt + vatAmt + tdAmt) - taxAmt) > 0.05) {
                                                    const calcSumNoTd = scAmt + mfAmt + vatAmt;
                                                    if (taxAmt >= calcSumNoTd) {
                                                        tdAmt = Number((taxAmt - calcSumNoTd).toFixed(2));
                                                    }
                                                }

                                                resolvedRoomUae = {
                                                    serviceCharge: scAmt,
                                                    serviceChargeRate: scRate,
                                                    municipalityFee: mfAmt,
                                                    municipalityRate: mfRate,
                                                    vat: vatAmt,
                                                    vatRate: vatRate,
                                                    tourismDirham: tdAmt,
                                                    tourismDirhamRate: tdRate
                                                };
                                            }
                                        }

                                        return (
                                            <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="text.primary" mb={1}>{roomLabel}</Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Room Rate</Typography>
                                                    <Typography variant="caption" fontWeight="600">{formatCurrency(roomRate, currency)}</Typography>
                                                </Box>
                                                {discount > 0 && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="caption" color="success.main">Discount Applied</Typography>
                                                        <Typography variant="caption" fontWeight="600" color="success.main">-{formatCurrency(discount, currency)}</Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Net Taxable Value</Typography>
                                                    <Typography variant="caption" fontWeight="700">{formatCurrency(netTaxable, currency)}</Typography>
                                                </Box>
                                                {isUae && resolvedRoomUae ? (
                                                    <Box sx={{ my: 1, p: 1, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Service Charge ({resolvedRoomUae.serviceChargeRate}%)
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                {formatCurrency(resolvedRoomUae.serviceCharge, currency)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Municipality Fee ({resolvedRoomUae.municipalityRate}%)
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                {formatCurrency(resolvedRoomUae.municipalityFee, currency)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                UAE VAT ({resolvedRoomUae.vatRate}%)
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                {formatCurrency(resolvedRoomUae.vat, currency)}
                                                            </Typography>
                                                        </Box>
                                                        {resolvedRoomUae.tourismDirham > 0 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                    Tourism Dirham Fee
                                                                </Typography>
                                                                <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                    {formatCurrency(resolvedRoomUae.tourismDirham, currency)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ fontSize: '0.78rem' }}>
                                                                Total Dubai Levies & Taxes
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="700" color="#2563eb" sx={{ fontSize: '0.78rem' }}>
                                                                {formatCurrency(taxAmt, currency)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Room Tax Amount {isRoomTaxExempt ? '(Exempt)' : (isUae ? '(Taxes & VAT)' : (gstPct > 0 ? `(${gstPct}% GST)` : ''))}
                                                        </Typography>
                                                        <Typography variant="caption" fontWeight="600" color="#2563eb">{formatCurrency(taxAmt, currency)}</Typography>
                                                    </Box>
                                                )}
                                                <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" fontWeight="bold">Room Total</Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#16a34a">{formatCurrency(finalTotal, currency)}</Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Paper>
                            )}
                        </Grid>

                        {/* RIGHT COLUMN: Rooms, Folio, Mod History, Notifications */}
                        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {/* 3. Room Allocations & Status Grid */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }} elevation={0}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Room Allocations Grid</Typography>
                                    {onMoveRoomClick && (
                                        <Button size="small" variant="text" onClick={() => onMoveRoomClick(data)}>Change/Move Room</Button>
                                    )}
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Room</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Occupancy</TableCell>
                                            <TableCell>Rate Plan</TableCell>
                                            <TableCell>Extra Bed</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.rooms || [data]).map((r, i) => {
                                            const roomName = r.room?.roomNumber || r.roomNumber || roomsList.find(x => x.id === r.roomId)?.roomNumber || r.roomId || 'Unassigned';
                                            const roomTypeName = r.roomType?.name || r.room?.type || roomTypesList.find(x => x.id === r.roomTypeId)?.name || 'Standard';
                                            const ratePlanName = ratePlansList.find(x => x.id === r.ratePlanId)?.name || ratePlansList.find(x => x.id === r.ratePlanId)?.ratePlanName || r.pricingSource || 'Base Rate';
                                            return (
                                                <React.Fragment key={r.id || i}>
                                                <TableRow>
                                                    <TableCell>{roomName}</TableCell>
                                                    <TableCell>{roomTypeName}</TableCell>
                                                    <TableCell>{r.adultsCount || 1}A / {r.kidsCount || 0}K</TableCell>
                                                    <TableCell>
                                                        {ratePlanName}
                                                        {r.rateOverride?.type && (
                                                            <Chip size="small" label={r.rateOverride.type} color="primary" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {hasRoomExtraBed(r, data) ? (
                                                            <Chip size="small" label="Yes" color="success" sx={{ fontWeight: 'bold', height: 22 }} />
                                                        ) : (
                                                            <Chip size="small" label="No" variant="outlined" sx={{ height: 22, color: 'text.secondary' }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell><Chip size="small" label={r.status || 'Confirmed'} color="default" /></TableCell>
                                                </TableRow>
                                                {r.dailyRates && r.dailyRates.length > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ p: 0, pb: 1, px: 2, borderBottom: '1px solid #e2e8f0' }}>
                                                            <Box sx={{ bgcolor: '#f8fafc', p: 1, borderRadius: 1, border: '1px solid #e2e8f0', mt: 0.5, mb: 1 }}>
                                                                <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>Nightly Breakdown</Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
                                                                    {r.dailyRates.map((d, di) => (
                                                                        <Box key={di} sx={{ minWidth: 80, p: 0.5, bgcolor: 'white', border: '1px solid #cbd5e1', borderRadius: 1, textAlign: 'center' }}>
                                                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{new Date(d.date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</Typography>
                                                                            <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>{formatCurrency(d.overrideRate !== undefined ? d.overrideRate : d.baseRate, currency)}</Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>

                            {/* 4. Live Folio Charges Tracking Sheet */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }} elevation={0}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Live Folio Charges Tracking Sheet</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {(() => {
                                    const folioItems = getAllFolioItems(data);
                                    const charges = folioItems.filter(i => !i.isVoided && (i.type === 'Charge' || i.category === 'Charge' || i.category === 'Other / Miscellaneous'));
                                    const displayCharges = charges.length > 0 ? charges : (data.folioCharges || []);
                                    return displayCharges.length > 0 ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Room</TableCell>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayCharges.map((c, i) => {
                                                    const roomObj = (data.rooms || []).find(r => r.id === c.reservationRoomId || r.roomId === c.reservationRoomId);
                                                    const roomNum = roomObj?.room?.roomNumber || roomObj?.roomNumber || 'All';
                                                    return (
                                                        <TableRow key={i} sx={{ opacity: c.isVoided ? 0.5 : 1 }}>
                                                            <TableCell>{formatDate(c.date || c.createdAt)}</TableCell>
                                                            <TableCell sx={{ textDecoration: c.isVoided ? 'line-through' : 'none' }}>
                                                                {c.description || c.item || c.category}
                                                                {c.isVoided && <Typography variant="caption" color="error" display="block">(Voided: {c.voidReason})</Typography>}
                                                            </TableCell>
                                                            <TableCell><Chip size="small" label={roomNum} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} /></TableCell>
                                                            <TableCell><Chip size="small" label={c.category || c.type || 'Add-on'} /></TableCell>
                                                            <TableCell align="right" sx={{ textDecoration: c.isVoided ? 'line-through' : 'none' }}>{formatCurrency(c.amount, currency)}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1 }}>
                                            No on-property incidental charges or room service logs generated yet.
                                        </Typography>
                                    );
                                })()}
                            </Paper>

                            {/* Rate History Audit Trail */}
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }} elevation={0}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Rate History Audit Trail</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {(data.rateOverrides || []).length > 0 ? (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Scope / Type</TableCell>
                                                <TableCell>Original Rate</TableCell>
                                                <TableCell>Overridden Rate</TableCell>
                                                <TableCell>Reason</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(data.rateOverrides || []).map((ro, i) => {
                                                let displayReason = ro.reasonCategory || ro.reason || ro.overrideReason || 'Discount';
                                                let remarks = '';
                                                let parsedType = ro.overrideType || 'EntireStay';
                                                try {
                                                    if (ro.reasonText && ro.reasonText.startsWith('{')) {
                                                        const parsed = JSON.parse(ro.reasonText);
                                                        displayReason = parsed.reason || displayReason;
                                                        remarks = parsed.remarks || '';
                                                        parsedType = parsed.type || parsedType;
                                                    } else if (ro.reasonText) {
                                                        remarks = ro.reasonText;
                                                    }
                                                } catch(e) {}

                                                const targetRoom = ro.reservationRoomId ? (data.rooms || []).find(r => r.id === ro.reservationRoomId || r.roomId === ro.reservationRoomId) : null;
                                                const targetScope = targetRoom ? `Room ${targetRoom.room?.roomNumber || targetRoom.roomNumber || 'Specific'}` : 'Master Reservation';

                                                return (
                                                    <TableRow key={i}>
                                                        <TableCell>{formatDate(ro.createdAt || ro.date || ro.timestamp)}</TableCell>
                                                        <TableCell>
                                                            <Chip size="small" label={targetScope} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                                            {parsedType && <Typography variant="caption" color="text.secondary" display="block">{parsedType}</Typography>}
                                                        </TableCell>
                                                        <TableCell>{ro.originalRate ? formatCurrency(Number(ro.originalRate), currency) : '-'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{ro.overriddenRate ? formatCurrency(Number(ro.overriddenRate), currency) : '-'}</TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{displayReason}</Typography>
                                                            {remarks && <Typography variant="caption" color="text.secondary">{remarks}</Typography>}
                                                        </TableCell>
                                                        <TableCell><Chip size="small" label={ro.status || 'Active'} color={ro.status === 'Approved' || ro.status === 'Active' ? 'success' : ro.status === 'Pending' ? 'warning' : ro.status === 'Rejected' ? 'error' : 'default'} /></TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1 }}>
                                        No rate overrides or modifications recorded.
                                    </Typography>
                                )}
                            </Paper>

                            <Grid container spacing={3}>
                                {/* 6. Chronological Modification Timeline */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }} elevation={0}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Modification Audit Trail</Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        {(data.modificationHistory || data.moveHistory?.length > 0) ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {[...(data.modificationHistory || []), ...(data.moveHistory || [])].sort((a,b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)).map((m, i) => (
                                                    <Box key={i} sx={{ borderLeft: '3px solid #3b82f6', pl: 2, py: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">{new Date(m.timestamp || m.createdAt).toLocaleString()}</Typography>
                                                        <Typography variant="body2">{m.action || m.event || 'Reservation Updated'}</Typography>
                                                        {m.details && <Typography variant="caption" color="text.secondary">{m.details}</Typography>}
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                                No modifications logged.
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* 7. Notification Logs Tray */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }} elevation={0}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Notification Logs</Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        {(data.notificationHistory || []).length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {(data.notificationHistory || []).map((n, i) => {
                                                    // Strip HTML tags to create a clean preview
                                                    const cleanMessage = typeof n.message === 'string'
                                                        ? n.message.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim()
                                                        : '';
                                                    const displayTitle = n.title || n.subject || (cleanMessage ? (cleanMessage.slice(0, 60) + '...') : 'Notification');
                                                    const displayChannel = n.channel || (n.type && n.type !== 'Info' ? n.type : 'Email');
                                                    const displayStatus = n.deliveryStatus || n.status || 'Sent';

                                                    return (
                                                        <Box key={i} sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="caption" fontWeight="bold" color="primary.main">{displayChannel}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{formatDate(n.sentAt || n.createdAt)}</Typography>
                                                            </Box>
                                                            <Typography variant="body2" fontWeight="600">{displayTitle}</Typography>
                                                            {cleanMessage && cleanMessage !== displayTitle && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.5 }}>
                                                                    {cleanMessage}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                Status: <strong style={{ color: displayStatus === 'Failed' ? '#d32f2f' : '#2e7d32' }}>{displayStatus}</strong>
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                                No communications dispatched yet.
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                    <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100 }}>Close</Button>
                </Box>
            </Box>
        </Modal>

        {/* Rejection Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => !actionLoading && setRejectDialogOpen(false)}>
            <DialogTitle>Reject Rate Override</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please provide a reason for rejecting this rate override request.
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Rejection Reason"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    disabled={actionLoading}
                    multiline
                    rows={3}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
                <Button onClick={handleRejectRate} color="error" variant="contained" disabled={actionLoading || !rejectionReason.trim()}>
                    {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Reject'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* ID Image Popup Dialog */}
        <Dialog open={!!idImagePopup} onClose={() => setIdImagePopup(null)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                View Uploaded ID
                <IconButton onClick={() => setIdImagePopup(null)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                {idImagePopup && (
                    <Box component="img" src={idImagePopup} alt="Uploaded ID" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                )}
            </DialogContent>
        </Dialog>

        {/* Government Approval Modal */}
        <ApproveGovernmentModal
            open={govModalOpen}
            onClose={() => setGovModalOpen(false)}
            reservation={data}
            tenantId={tenantId}
            onSuccess={(id) => {
                fetchDetail(id);
                if (onClearanceApproved) onClearanceApproved(id);
            }}
            setNotification={setNotification}
        />
        </>
    );
}
